import React, { ReactElement, useContext, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  AllCommunityModule,
  themeAlpine,
} from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import { BsFillSendCheckFill } from "react-icons/bs";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import AgGridPagination from "../../shared/AgGridPagination";
import ReceiverAccountDetailsModal from "../../shared/ReceiverAccountDetailsModal";
import ResendAccountConfirmModal from "../../receiver/components/ResendAccountConfirmModal";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type ReceiverAccountRow = {
  receiver_id: string;
  account_id: string;
  asset: string;
  method_name: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  confirm_status?: string;
  created_at?: string;
  method_details?: Record<string, any>;
  [key: string]: any;
};

interface Props {
  isLoading: boolean;
  tableData: ReceiverAccountRow[] | null;
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  currentLastKey: string;
}

const statusClasses = "status";

interface CustomerStatusItems {
  status: string;
  value: string;
  display: ReactElement;
}

const statusList: CustomerStatusItems[] = [
  {
    status: "unconfirmed",
    value: "unconfirmed",
    display: (
      <Badge className={`${statusClasses} status-unconfirmed`}>
        Unconfirmed
      </Badge>
    ),
  },
  {
    status: "sent_mail",
    value: "sent_mail",
    display: (
      <Badge className={`${statusClasses} status-sent_mail`}>
        Email Sent
      </Badge>
    ),
  },
  {
    status: "confirmed",
    value: "confirmed",
    display: (
      <Badge className={`${statusClasses} status-confirmed`}>Confirmed</Badge>
    ),
  },
];

const renderConfirmStatus = (status?: string): ReactElement | null => {
  if (!status) return null;
  const statusItem = statusList.find((item) => item.status === status);
  return statusItem ? statusItem.display : null;
};

const capitalizeLabel = (label: string) => {
  let modifiedLabel = label
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  switch (label) {
    case "bsb":
      modifiedLabel = "BSB";
      break;
    case "bic":
      modifiedLabel = "BIC";
      break;
    case "iban":
      modifiedLabel = "IBAN";
      break;
  }

  return modifiedLabel;
};

const CustomLoadingOverlay = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
    }}
  >
    <CircularProgress size={40} />
  </div>
);

const ReceiverAccountSearchList: React.FC<Props> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReceiverAccountRow | null>(
    null
  );
  const [isOpenResendAccountConfirmModal, setIsOpenResendAccountConfirmModal] =
    useState<boolean>(false);
  const [
    receiverAccountDetailForResendConfirm,
    setReceiverAccountDetailForResendConfirm,
  ] = useState<any>(null);
  const [swalProps, setSwalProps] = useState<any>({});

  const handleResendAccount = (row: ReceiverAccountRow) => {
    setReceiverAccountDetailForResendConfirm({ ...row, id: row.account_id });
    setIsOpenResendAccountConfirmModal(true);
  };

  const closeResendAccountConfirmModal = () => {
    setReceiverAccountDetailForResendConfirm(null);
    setIsOpenResendAccountConfirmModal(false);
  };

  const handleCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy id: ", err);
    }
  };

  const formatDate = (timestamp?: string) => {
    if (!timestamp) return "-";
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const formatName = (row: ReceiverAccountRow) => {
    const parts = [row.first_name, row.middle_name, row.last_name].filter(
      (p) => p && String(p).trim().length > 0
    );
    return parts.length > 0 ? parts.join(" ") : "-";
  };

  const formatMethodDetails = (details?: Record<string, any>) => {
    if (!details || typeof details !== "object") return "-";
    const parts = Object.entries(details)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([k, v]) => `${capitalizeLabel(k)}: ${v}`);
    return parts.length > 0 ? parts.join(" • ") : "-";
  };

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy Receiver Account ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-account-id-" + params.data?.account_id}
          onClick={() => handleCopy(params.data.account_id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const ConfirmStatusRenderer = (params: ICellRendererParams) => (
    <>{renderConfirmStatus(params.data.confirm_status)}</>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <>
      {params.data?.confirm_status === "sent_mail" &&
        userRole === "superadmin" && (
          <Tooltip title="Resend Confirmation Link" arrow placement="top">
            <IconButton
              id={"button-resend-account-confirm-" + params.data?.account_id}
              onClick={() => handleResendAccount(params.data)}
              size="small"
            >
              <BsFillSendCheckFill className="h-5 w-5 text-navy-700 opacity-90" />
            </IconButton>
          </Tooltip>
        )}
      <Tooltip title="Details" arrow placement="top">
        <IconButton
          id={"button-details-" + params.data?.account_id}
          onClick={() => {
            setSelectedRow(params.data);
            setIsDetailsOpen(true);
          }}
          size="small"
        >
          <AiOutlineInfoCircle className="h-5 w-5 text-navy-700" />
        </IconButton>
      </Tooltip>
    </>
  );

  const textCellStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  const columns: ColDef[] = [
    {
      field: "account_id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      minWidth: 160,
      flex: 1,
      valueGetter: (params: any) => formatDate(params.data?.created_at),
      tooltipValueGetter: (params: any) => formatDate(params.data?.created_at),
      cellStyle: textCellStyle,
    },
    {
      field: "name",
      headerName: "Receiver Name",
      sortable: false,
      minWidth: 160,
      flex: 1.2,
      valueGetter: (params: any) => formatName(params.data),
      tooltipValueGetter: (params: any) => formatName(params.data),
      cellStyle: textCellStyle,
    },
    {
      field: "email",
      headerName: "Email",
      sortable: false,
      minWidth: 180,
      flex: 1.5,
      tooltipField: "email",
      cellStyle: textCellStyle,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 90,
      tooltipField: "asset",
      cellStyle: textCellStyle,
    },
    {
      field: "method_name",
      headerName: "Method",
      sortable: false,
      minWidth: 130,
      flex: 1,
      tooltipField: "method_name",
      cellStyle: textCellStyle,
    },
    {
      field: "method_details",
      headerName: "Method Details",
      sortable: false,
      minWidth: 320,
      flex: 3,
      valueGetter: (params: any) =>
        formatMethodDetails(params.data?.method_details),
      tooltipValueGetter: (params: any) =>
        formatMethodDetails(params.data?.method_details),
      cellStyle: textCellStyle,
    },
    {
      field: "confirm_status",
      headerName: "Confirm Status",
      sortable: false,
      width: 140,
      cellRenderer: ConfirmStatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 110,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  const rowData = isLoading || !tableData ? [] : tableData;

  // The list only renders post-submit (parent gates it via CSSTransition),
  // so the no-rows state is always "search returned nothing."
  const noRowsMessage = "No receiver accounts found.";

  return (
    <div>
      <Box className="card mt-3 w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={rowData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.account_id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
            suppressDragLeaveHidesColumns={true}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate={noRowsMessage}
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      {isDetailsOpen && selectedRow && (
        <ReceiverAccountDetailsModal
          isOpen={isDetailsOpen}
          onClose={() => {
            setIsDetailsOpen(false);
            setSelectedRow(null);
          }}
          detail={selectedRow}
          receiverId={selectedRow.receiver_id}
        />
      )}

      {isOpenResendAccountConfirmModal &&
        receiverAccountDetailForResendConfirm && (
          <ResendAccountConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={closeResendAccountConfirmModal}
            receiverAccountToResendConfirm={
              receiverAccountDetailForResendConfirm
            }
            isOpen={isOpenResendAccountConfirmModal}
            onClose={closeResendAccountConfirmModal}
          />
        )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default ReceiverAccountSearchList;
